/**
 * appt-setup.tsx — the Setup rail for an APPOINTMENT journey (2026-08-31).
 *
 * Its own module rather than seven more panels inside NewJourneys.tsx, which is
 * already 4,300 lines. The host passes the document, a patcher and the live
 * libraries; everything else is here.
 *
 * THE SHAPE OF THIS SURFACE IS DECIDED BY ONE FACT: an appointment journey has
 * no record (`APPOINTMENT_BINDING.ownsRecord: false`). It is synthesised, one
 * document per line of business, from whichever catalogue services are bookable.
 * So Setup splits three ways and every control says which it is:
 *
 *   A · journey-owned  → apptSetup on the document, written by the host's patch
 *   B · referenced     → catalogue truth, shown read-only with a jump. Editing
 *                        it here would change every queue and funnel that reads
 *                        the same service row.
 *   C · blocked        → needs a field that does not exist yet. Rendered as a
 *                        disabled row that says what it will do, never as a
 *                        control that silently does nothing.
 *
 * Service-scoped settings (questions, and — once the API lands — surveys,
 * promos and flows) appear here as a per-service MATRIX and never on a page,
 * because at design time nobody knows which service the customer will pick.
 */
import { useMemo } from "react";
import { CatalogPicker, resolveSelection, type PickerItem, type PickerGroup, type PickerSel } from "./catalog-picker";
import { Btn, Icon, T } from "@v0y4ge/jrni-ui";
import type { ApptSetupLike, Doc } from "./nj-model";

import { t, t as i18nT, useT } from "@v0y4ge/i18n-runtime";
/* ── the shapes the host hands in ─────────────────────────────────────── */
export interface ApptService {
  id: string; name: string; category?: string; lob?: string;
  durationMin?: number | null; preMin?: number; postMin?: number;
  channels?: { inPerson: boolean; phone: boolean; video: boolean };
  questionGroupId?: string | null;
  minAdvance?: string | null; maxAdvance?: string | null; minCancel?: string | null;
  disableBefore?: string | null; disableAfter?: string | null;
}
export interface ApptLibs {
  services: ApptService[];
  categories: { id: string; name: string }[];
  locations: { id: string; name: string; address?: string }[];
  questionGroups: { id: string; name: string; count: number }[];
  surveys: { id: string; name: string }[];
  promos: { id: string; label: string }[];
  flows: { id: string; name: string }[];
  /** Real availability for the funnel's slug, or null when it has no slug yet. */
  availability?: { days: number; slots: number } | null;
}
export type ApptView = "aservices" | "aplaces" | "awindow" | "aperservice" | "adetails" | "amessaging" | "publish";

/** Setup tab labels by view id. Each value is a getter, so a spread or a read at render gets the
 *  viewer's language rather than the language at import. */
export const APPT_SETUP_LABEL: Record<string, string> = {
  get aservices() { return i18nT("journeys.apptSetup.tab.services", "Services"); },
  get aplaces() { return i18nT("journeys.apptSetup.tab.places", "Places & methods"); },
  get awindow() { return i18nT("journeys.apptSetup.tab.window", "Booking window"); },
  get aperservice() { return i18nT("journeys.apptSetup.tab.perService", "Per-service settings"); },
  get adetails() { return i18nT("journeys.apptSetup.tab.details", "Details & consent"); },
  get amessaging() { return i18nT("journeys.apptSetup.tab.messaging", "Messaging"); },
};

export const APPT_SETUP_DEFAULTS: ApptSetupLike = {
  serviceSelection: [], groupByCategory: false, locationIds: [],
  methods: { inPerson: true, phone: false, video: false },
  standardFields: ["email"], consent: {}, // ui-config: stored field ids
};

/** Readiness, declared ONCE and read by the tab dot, the in-panel band and the
 *  publish gate — so the three cannot disagree, same rule as the other types. */
export function apptReadiness(a: Partial<ApptSetupLike>, doc: Doc): { tab: ApptView; label: string; done: boolean }[] {
  const m = a.methods ?? APPT_SETUP_DEFAULTS.methods;
  const remote = !!m.phone || !!m.video;
  return [
    { tab: "aservices", label: t('journeys.apptSetup.label', "At least one service offered"), done: (a.serviceSelection ?? []).length > 0 },
    { tab: "aplaces", label: t('journeys.apptSetup.label2', "A place, or a method that needs none"), done: (a.locationIds ?? []).length > 0 || remote },
    { tab: "adetails", label: t('journeys.apptSetup.label3', "Email captured on the About you page"), done: (a.standardFields ?? []).indexOf("email") > -1 },
    { tab: "publish", label: t('journeys.apptSetup.label4', "A publish slug"), done: !!doc.publish?.slug },
  ];
}

/* ── local style vocabulary, matching the ported Setup chrome ─────────── */
const FONT = 'var(--jrni-font-family-sans, "DM Sans", "Helvetica Neue", sans-serif)';
const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#333333", display: "block", marginBottom: 5 };
const input: React.CSSProperties = { width: "100%", height: 34, padding: "0 10px", border: "1px solid #D1D4DC", borderRadius: 8, fontSize: 12.5, fontFamily: FONT, boxSizing: "border-box", background: "#FFFFFF" };
const card: React.CSSProperties = { maxWidth: 620, display: "flex", flexDirection: "column", gap: 16 };
const sec: React.CSSProperties = { fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#697180", marginBottom: 8 };
const note: React.CSSProperties = { fontSize: 11.5, color: "#697180", lineHeight: 1.6 };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, Menlo, monospace" };

/** The class badge every control carries, so an operator always knows whether a
 *  value is theirs, the catalogue's, or not yet possible. */
function Owned({ cls }: { cls: "A" | "B" | "C" }) {
  const t = useT();
  const m = {
    A: { t: t("journeys.apptSetup.owned.thisFunnel", "This funnel"), bg: "#EEF6EF", fg: "#1E5C3F" },
    B: { t: t("journeys.apptSetup.owned.catalogueReadOnly", "Catalog · read-only"), bg: "#F1EFFC", fg: "var(--jrni-color-primary-text, #5142C7)" },
    C: { t: t("journeys.apptSetup.owned.needsApi", "Needs the API"), bg: "#FFF7E8", fg: "#8A5A08" },
  }[cls];
  return <span style={{ fontSize: 9.5, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: m.bg, color: m.fg, whiteSpace: "nowrap" }}>{m.t}</span>;
}

function Row({ title, cls, children, sub }: { title: string; cls: "A" | "B" | "C"; children?: React.ReactNode; sub?: string }) {
  return (
    <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: "12px 14px", background: "#FFFFFF" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: sub || children ? 7 : 0, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, flex: 1, minWidth: 0 }}>{title}</span>
        <Owned cls={cls} />
      </div>
      {sub ? <div style={{ ...note, marginBottom: children ? 8 : 0 }}>{sub}</div> : null}
      {children}
    </div>
  );
}

export interface ApptSetupProps {
  view: ApptView;
  doc: Doc;
  setup: Partial<ApptSetupLike>;
  libs: ApptLibs;
  onPatch: (patch: Partial<ApptSetupLike>) => void;
  onPublish: (patch: Record<string, string>) => void;
  goRoute: (route: string, params?: Record<string, string>) => void;
}

export function ApptSetupPanel({ view, doc, setup, libs, onPatch, onPublish, goRoute }: ApptSetupProps) {
  const t = useT();
  const a = { ...APPT_SETUP_DEFAULTS, ...setup };
  const chosen = a.serviceSelection ?? [];
  /** Picker items and groups: services grouped by their line of business, which
   *  is what the operator asked every picker in the designer to show. */
  const svcItems: PickerItem[] = libs.services.map((s) => {
    const ch = s.channels ?? { inPerson: true, phone: false, video: false };
    const methods = [ch.inPerson ? t("journeys.apptSetup.method.inPerson", "In person") : null, ch.phone ? t("journeys.apptSetup.method.phone", "Phone") : null, ch.video ? t("journeys.apptSetup.method.video", "Video") : null].filter(Boolean).join(" · ");
    return {
      id: s.id, name: s.name,
      group: s.lob ?? s.category ?? null,
      meta: [s.durationMin ? t("journeys.apptSetup.durationMin", "{minutes} min", { minutes: s.durationMin }) : t("journeys.apptSetup.noDuration", "no duration set"), methods || null],
      keywords: (s as any).description ?? "",
    };
  });
  const svcGroups: PickerGroup[] = (libs.categories ?? []).map((c, i) => ({ id: c.id, name: c.name, hue: ["#0AA396", "#2D60EB", "#D61A55", "#916D0F", "#7C3AED"][i % 5]! }));
  /* A whole-department rule is stored as a `dept:` id inside the SAME string[]
     the funnel already persisted, so no stored shape changes and an older
     funnel round-trips untouched. */
  const DEPT = "dept:";
  const pickerValue: PickerSel[] = chosen.map((id) => (id.startsWith(DEPT) ? { type: "group", id: id.slice(DEPT.length) } : { type: "item", id }) as PickerSel);
  const commitPicker = (next: PickerSel[]) =>
    onPatch({ serviceSelection: next.map((s) => (s.type === "group" ? DEPT + s.id : s.id)) });
  const offered = useMemo(
    () => resolveSelection(pickerValue, svcItems)
      .map((it) => libs.services.filter((s) => s.id === it.id)[0])
      .filter(Boolean) as ApptService[],
    [chosen.join("|"), libs.services],
  );

  /* toggleService is gone with the flat checkbox list — CatalogPicker owns the
     add/remove/reorder and commitPicker is the only writer. */

  /* ── 1 · Services ─────────────────────────────────────────────────── */
  if (view === "aservices") {
    return (
      <div style={card}>
        <div>
          <div style={sec}>{t('journeys.apptSetup.text', "Services this funnel offers")}</div>
          <div style={note}>{t('journeys.apptSetup.text2', "Add whole departments or single services — the order here is the order on the Book a service page. Duration and delivery methods come from the catalog and are shown so you can see what you are offering; they are not editable from inside one funnel.")}</div>
        </div>
        {/* The one picker (2026-09-02, operator): the same component the queue,
            One Stop and ticket pickers use. This was a flat checkbox list whose
            order was click order shown as a number badge — no departments, no
            search, no way to take a whole line of business. */}
        <CatalogPicker
          items={svcItems}
          groups={svcGroups}
          value={pickerValue}
          onChange={commitPicker}
          sourceLabel={t("journeys.apptSetup.picker.source", "Your catalog")}
          selectedLabel={t("journeys.apptSetup.picker.selected", "This funnel offers")}
          noun="service"
          searchPlaceholder={t("journeys.apptSetup.picker.search", "Search services or departments…")}
          emptySource={t("journeys.apptSetup.picker.emptySource", "No bookable services in this line of business yet.")}
          emptySelection={t("journeys.apptSetup.picker.emptySelection", "Pick what this funnel books — they appear here in the order the customer sees.")} />
        {libs.services.length === 0 ? (
          <div style={note}>
            <a href="#" onClick={(e) => { e.preventDefault(); goRoute("ops.catalogue"); }}>{t('journeys.apptSetup.text3', "Open the catalog")}</a> {t('journeys.apptSetup.text4', "to mark some services bookable.")}</div>
        ) : null}
        <Row title={t('journeys.apptSetup.title', "Group services by category on the page")} cls="A">
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: "pointer" }}>
            <input type="checkbox" checked={!!a.groupByCategory} onChange={(e) => onPatch({ groupByCategory: e.target.checked })} />
            {t('journeys.apptSetup.text5', "Show category headings")}</label>
        </Row>
        <Row title={t('journeys.apptSetup.title2', "Duration, buffers and price")} cls="B"
          sub={t('journeys.apptSetup.sub', "One catalog row per service, shared with queueing and events. Changing a duration here would change it everywhere.")}>
          <Btn size="sm" variant="secondary" onClick={() => goRoute("ops.catalogue")}>{t('journeys.apptSetup.text3', "Open the catalog")}</Btn>
        </Row>
      </div>
    );
  }

  /* ── 2 · Places & methods ─────────────────────────────────────────── */
  if (view === "aplaces") {
    const ceiling = offered.reduce(
      (acc, s) => {
        const ch = s.channels ?? { inPerson: true, phone: false, video: false };
        return { inPerson: acc.inPerson || ch.inPerson, phone: acc.phone || ch.phone, video: acc.video || ch.video };
      },
      { inPerson: false, phone: false, video: false },
    );
    const methodRow = (k: "inPerson" | "phone" | "video", name: string) => {
      const allowed = offered.length === 0 || ceiling[k];
      const on = !!a.methods[k];
      return (
        <label key={k} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, padding: "8px 10px", borderRadius: 8, border: "1px solid #E6E8EC", opacity: allowed ? 1 : 0.5, cursor: allowed ? "pointer" : "not-allowed" }}>
          <input type="checkbox" checked={on && allowed} disabled={!allowed} onChange={(e) => onPatch({ methods: { ...a.methods, [k]: e.target.checked } })} />
          <span style={{ flex: 1 }}>{name}</span>
          {!allowed ? <span style={{ fontSize: 10.5, color: "#8A5A08" }}>{t('journeys.apptSetup.text6', "no offered service supports it")}</span> : null}
        </label>
      );
    };
    return (
      <div style={card}>
        <div>
          <div style={sec}>{t('journeys.apptSetup.text7', "Where and how")}</div>
          <div style={note}>{t('journeys.apptSetup.text8', "A funnel cannot offer a method none of its services supports — those rows are disabled rather than selectable and silently dropped at booking time.")}</div>
        </div>
        <Row title={t('journeys.apptSetup.title3', "Delivery methods")} cls="A">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {methodRow("inPerson", t("journeys.apptSetup.method.inPerson", "In person"))}
            {methodRow("phone", t("journeys.apptSetup.method.phone", "Phone"))}
            {methodRow("video", t("journeys.apptSetup.method.video", "Video"))}
          </div>
        </Row>
        <Row title={t('journeys.apptSetup.title4', "Locations offered")} cls="A" sub={t('journeys.apptSetup.sub2', "Leave all unticked to offer every location these services are available at.")}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {libs.locations.length === 0 ? <span style={note}>{t('journeys.apptSetup.text9', "No locations in the library yet.")}</span> : libs.locations.map((l) => {
              const on = (a.locationIds ?? []).indexOf(l.id) > -1;
              return (
                <button key={l.id} onClick={() => onPatch({ locationIds: on ? (a.locationIds ?? []).filter((x) => x !== l.id) : (a.locationIds ?? []).concat(l.id) })}
                  style={{ padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, border: `1.5px solid ${on ? "var(--jrni-color-primary-text, #5142C7)" : "#E6E8EC"}`, background: on ? "var(--jrni-color-primary-text, #5142C7)" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563" }}>
                  {l.name}
                </button>
              );
            })}
          </div>
        </Row>
        <Row title={t('journeys.apptSetup.title5', "Opening hours")} cls="B" sub={t('journeys.apptSetup.sub3', "A location's hours bound every slot at it. Edited in the locations library.")}>
          <Btn size="sm" variant="secondary" onClick={() => goRoute("locations")}>{t('journeys.apptSetup.text10', "Open locations")}</Btn>
        </Row>
      </div>
    );
  }

  /* ── 3 · Booking window ───────────────────────────────────────────── */
  if (view === "awindow") {
    return (
      <div style={card}>
        <div>
          <div style={sec}>{t('journeys.apptSetup.text11', "The rules around the times")}</div>
          <div style={note}>{t('journeys.apptSetup.text12', "Nothing here sets a time. Slots are computed from advisor working patterns, service duration and buffers, and location hours — what Setup owns is how far ahead booking opens and how late it closes, and today those are catalog fields per service.")}</div>
        </div>
        {offered.length === 0 ? (
          <div style={{ border: "1px dashed #D1D4DC", borderRadius: 9, padding: "18px 12px", textAlign: "center", ...note }}>{t('journeys.apptSetup.text13', "Pick services first — the windows are per service.")}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {offered.map((s) => (
              <div key={s.id} style={{ border: "1px solid #E6E8EC", borderRadius: 9, padding: "10px 12px", background: "#FFFFFF" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, flex: 1, minWidth: 0 }}>{s.name}</span>
                  <Owned cls="B" />
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 14px", fontSize: 11, color: "#697180" }}>
                  <span>{t("journeys.apptSetup.window.opens", "Opens {value} ahead", { value: s.minAdvance ?? "—" })}</span>
                  <span>{t("journeys.apptSetup.window.closes", "Closes {value} out", { value: s.maxAdvance ?? "—" })}</span>
                  <span>{t("journeys.apptSetup.window.cancelBy", "Cancel by {value}", { value: s.minCancel ?? "—" })}</span>
                  {s.disableBefore ? <span>{t("journeys.apptSetup.window.from", "from {date}", { date: s.disableBefore })}</span> : null}
                  {s.disableAfter ? <span>{t("journeys.apptSetup.window.until", "until {date}", { date: s.disableAfter })}</span> : null}
                </div>
                <a href="#" onClick={(e) => { e.preventDefault(); goRoute("ops.catalogue", { service: s.id }); }} style={{ fontSize: 11, fontWeight: 700, display: "inline-block", marginTop: 6 }}>{t('journeys.apptSetup.text21', "Edit in the catalog →")}</a>
              </div>
            ))}
          </div>
        )}
        <Row title={t('journeys.apptSetup.title6', "A window for this funnel only")} cls="C"
          sub={t('journeys.apptSetup.sub4', "A funnel-specific notice period — shorter for walk-in-adjacent booking, longer for private advice — needs a per-funnel override the catalog cannot express. Requested; until it lands the catalog value applies everywhere.")}>
          <span style={{ ...note, ...mono, fontSize: 10.5 }}>{"CD-API-REQUEST-journey-availability-2026-08-14" /* ui-config: request id */}</span>
        </Row>
        <Row title={t('journeys.apptSetup.title7', "Availability preview")} cls="B"
          sub={doc.publish?.slug
            ? (libs.availability ? t("journeys.apptSetup.availability.summary", "{days, plural, one {# day} other {# days}} with {slots, plural, one {# bookable slot} other {# bookable slots}} in the next four weeks.", { days: libs.availability.days, slots: libs.availability.slots }) : t("journeys.apptSetup.availability.none", "No slots returned for this funnel yet — usually no advisor offers these services."))
            : t('journeys.apptSetup.sub5', "Publish a slug first — availability is read per funnel.")} />
      </div>
    );
  }

  /* ── 4 · Per-service settings ─────────────────────────────────────── */
  if (view === "aperservice") {
    const gName = (id?: string | null) => (id ? libs.questionGroups.filter((g) => g.id === id)[0] : null);
    return (
      <div style={{ ...card, maxWidth: 760 }}>
        <div>
          <div style={sec}>{t('journeys.apptSetup.text23', "What each service asks and sends")}</div>
          <div style={note}>{t('journeys.apptSetup.text24', "This is a matrix rather than a picker, and it is the reason these settings left the page designer: at design time nobody knows which service the customer will pick, so a single journey-wide value would be a confident answer to an unanswerable question. Questions are live today; surveys, promos and flows are requested.")}</div>
        </div>
        {offered.length === 0 ? (
          <div style={{ border: "1px dashed #D1D4DC", borderRadius: 9, padding: "18px 12px", textAlign: "center", ...note }}>{t('journeys.apptSetup.text25', "Pick services first.")}</div>
        ) : (
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1.3fr 1fr", gap: 10, padding: "8px 12px", background: "#F7F7FA", fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#697180" }}>
              <span>{t('journeys.apptSetup.text26', "Service")}</span><span>{t('journeys.apptSetup.text27', "Booking questions")}</span><span>{t('journeys.apptSetup.text28', "Survey · promo · flow")}</span>
            </div>
            {offered.map((s) => {
              const g = gName(s.questionGroupId);
              return (
                <div key={s.id} style={{ display: "grid", gridTemplateColumns: "1.3fr 1.3fr 1fr", gap: 10, padding: "10px 12px", borderTop: "1px solid #EEF0F3", alignItems: "baseline", fontSize: 11.5 }}>
                  <span style={{ fontWeight: 700 }}>{s.name}</span>
                  <span>
                    {g
                      ? <>{g.name} <span style={{ color: "#697180" }}>· {t("journeys.apptSetup.questionCount", "{count, plural, one {# question} other {# questions}}", { count: g.count })}</span><br /><a href="#" onClick={(e) => { e.preventDefault(); goRoute("questions.group.edit", { id: g.id }); }} style={{ fontSize: 11 }}>{t('journeys.apptSetup.text31', "Edit group →")}</a></>
                      : <span style={{ color: "#8A5A08" }}>{t('journeys.apptSetup.text32', "None set —")}{" "}<a href="#" onClick={(e) => { e.preventDefault(); goRoute("ops.catalogue", { service: s.id }); }}>{t('journeys.apptSetup.text33', "set one on the service")}</a></span>}
                  </span>
                  <span style={{ color: "#8A92A0", fontSize: 10.5, lineHeight: 1.5 }}>{t('journeys.apptSetup.text34', "Pending the catalog fields")}</span>
                </div>
              );
            })}
          </div>
        )}
        {/* Legacy rows — only where a value exists, and gone for good once
            removed (operator ruling). Withdrawing the picker must not silently
            leave a live setting with nowhere to see it. */}
        {a.legacySurveyId || a.legacyPromoId ? (
          <div style={{ border: "1px solid #F0DCB8", background: "#FFFBF2", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#8A5A08", marginBottom: 6 }}>{t('journeys.apptSetup.text35', "Attached before per-service settings")}</div>
            <div style={{ ...note, marginBottom: 9 }}>{t('journeys.apptSetup.text36', "Journey-wide attachments are no longer offered, but these are still live on this funnel. They keep working until you remove them; removing is permanent.")}</div>
            {a.legacySurveyId ? (
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6 }}>
                <span style={{ flex: 1, fontSize: 12 }}>{t("journeys.apptSetup.legacy.survey", "Survey · {name}", { name: libs.surveys.filter((x) => x.id === a.legacySurveyId)[0]?.name ?? a.legacySurveyId })}</span>
                <Btn size="sm" variant="secondary" danger onClick={() => onPatch({ legacySurveyId: null })}>{t('journeys.apptSetup.text38', "Remove")}</Btn>
              </div>
            ) : null}
            {a.legacyPromoId ? (
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ flex: 1, fontSize: 12 }}>{t("journeys.apptSetup.legacy.promo", "Promo · {name}", { name: libs.promos.filter((x) => x.id === a.legacyPromoId)[0]?.label ?? a.legacyPromoId })}</span>
                <Btn size="sm" variant="secondary" danger onClick={() => onPatch({ legacyPromoId: null })}>{t('journeys.apptSetup.text38', "Remove")}</Btn>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  /* ── 5 · Details & consent ────────────────────────────────────────── */
  if (view === "adetails") {
    const fields = a.standardFields ?? ["email"];
    const toggleField = (k: string) => onPatch({ standardFields: fields.indexOf(k) > -1 ? fields.filter((x) => x !== k) : fields.concat(k) });
    return (
      <div style={card}>
        <div>
          <div style={sec}>{t('journeys.apptSetup.text40', "The About you page")}</div>
          <div style={note}>{t('journeys.apptSetup.text41', "What the funnel asks beyond the service's own question group, and what the customer agrees to. Events keep these on the event record; an appointment funnel has no record, so they live on the journey.")}</div>
        </div>
        <Row title={t('journeys.apptSetup.title8', "Standard fields")} cls="A" sub={t('journeys.apptSetup.sub6', "Name is always captured. Email is required to send a confirmation.")}>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {[["email", t("journeys.apptSetup.field.email", "Email address")], ["phone", t("journeys.apptSetup.field.phone", "Mobile number")]].map(([k, name]) => (
              <label key={k} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, cursor: "pointer" }}>
                <input type="checkbox" checked={fields.indexOf(k!) > -1} onChange={() => toggleField(k!)} />
                {name}
              </label>
            ))}
          </div>
        </Row>
        <Row title={t('journeys.apptSetup.title9', "Consent")} cls="A">
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, cursor: "pointer" }}>
              <input type="checkbox" checked={!!a.consent?.marketing} onChange={(e) => onPatch({ consent: { ...a.consent, marketing: e.target.checked } })} />
              {t('journeys.apptSetup.text42', "Offer marketing opt-in")}</label>
            <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, cursor: "pointer" }}>
              <input type="checkbox" checked={!!a.consent?.contactSharing} onChange={(e) => onPatch({ consent: { ...a.consent, contactSharing: e.target.checked } })} />
              {t('journeys.apptSetup.text43', "Offer contact-sharing opt-in")}</label>
            <span style={label}>{t('journeys.apptSetup.text44', "Privacy policy URL")}</span>
            <input value={a.policyUrl ?? ""} onChange={(e) => onPatch({ policyUrl: e.target.value })} placeholder={
            // ui-config: a URL scheme hint, not copy
            "https://"} style={input} />
          </div>
        </Row>
        <Row title={t('journeys.apptSetup.title10', "Terms shown at booking")} cls="A">
          <textarea value={a.termsCopy ?? ""} onChange={(e) => onPatch({ termsCopy: e.target.value })} rows={3}
            style={{ ...input, height: "auto", padding: 9, resize: "vertical", lineHeight: 1.5 }} />
        </Row>
        <Row title={t('journeys.apptSetup.title11', "After booking, send them to")} cls="A" sub={t('journeys.apptSetup.sub7', "Leave empty to stay on the confirmation page.")}>
          <input value={a.redirectUrl ?? ""} onChange={(e) => onPatch({ redirectUrl: e.target.value })} placeholder={
            // ui-config: a URL scheme hint, not copy
            "https://"} style={input} />
        </Row>
      </div>
    );
  }

  /* ── 6 · Messaging ────────────────────────────────────────────────── */
  if (view === "amessaging") {
    return (
      <div style={card}>
        <div>
          <div style={sec}>{t('journeys.apptSetup.text45', "Fallback message flow")}</div>
          <div style={note}>{t('journeys.apptSetup.text46', "Once services carry their own flows, this is what runs for a service that names none — and for the moments with no service to resolve from at all. It is a fallback by design, not a limitation.")}</div>
        </div>
        <Row title={t('journeys.apptSetup.title12', "Flow for this funnel")} cls="A">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {libs.flows.length === 0 ? <span style={note}>{t('journeys.apptSetup.text47', "No flows in the library yet.")}</span> : libs.flows.map((f) => {
              const on = a.messagingFlowId === f.id;
              return (
                <div key={f.id} onClick={() => onPatch({ messagingFlowId: on ? undefined : f.id })}
                  style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 11px", borderRadius: 9, cursor: "pointer", background: on ? "var(--jrni-color-primary-4, #F6F2FE)" : "#FFFFFF", border: `1.5px solid ${on ? "var(--jrni-color-primary-1, #5142C7)" : "#E6E8EC"}` }}>
                  <span style={{ flex: 1, fontSize: 12.5, fontWeight: on ? 700 : 500 }}>{f.name}</span>
                  {on ? <Icon.check size={13} /> : null}
                </div>
              );
            })}
          </div>
        </Row>
        <Row title={t('journeys.apptSetup.title13', "A flow per service")} cls="C"
          sub={t('journeys.apptSetup.sub8', "Selected from the flows that already exist in Messaging — a dropdown per service, not a flow to build per service. Requested alongside per-service surveys and promos.")}>
          <span style={{ ...note, ...mono, fontSize: 10.5 }}>{"CD-API-REQUEST-service-scoped-attachments-2026-08-31" /* ui-config: request id */}</span>
        </Row>
      </div>
    );
  }

  /* ── 7 · Publish ──────────────────────────────────────────────────── */
  const pub = doc.publish ?? {};
  return (
    <div style={card}>
      <div>
        <div style={sec}>{t('journeys.apptSetup.text49', "Where this funnel lives")}</div>
        <div style={note}>{t('journeys.apptSetup.text50', "An appointment funnel has no record to switch live — publishing means its slug serves. Everything else on this page is how it presents when shared.")}</div>
      </div>
      <Row title={t('journeys.apptSetup.title14', "Slug")} cls="A" sub={t('journeys.apptSetup.sub9', "The path customers book at.")}>
        <input value={pub.slug ?? ""} onChange={(e) => onPublish({ slug: e.target.value })} placeholder={
          // ui-config: an example slug, not copy
          "book-with-us"} style={input} />
      </Row>
      <Row title={t('journeys.apptSetup.title15', "Host")} cls="A">
        <input value={pub.tenantHost ?? ""} onChange={(e) => onPublish({ tenantHost: e.target.value })} placeholder={
          // ui-config: an example host, not copy
          "booking.example.com"} style={input} />
      </Row>
      <Row title={t('journeys.apptSetup.title16', "SEO title")} cls="A">
        <input value={pub.seoTitle ?? ""} onChange={(e) => onPublish({ seoTitle: e.target.value })} style={input} />
      </Row>
      <Row title={t('journeys.apptSetup.title17', "Share image")} cls="A" sub={t('journeys.apptSetup.sub10', "Shown when the link is pasted into a chat or a post.")}>
        <input value={pub.shareImage ?? ""} onChange={(e) => onPublish({ shareImage: e.target.value })} placeholder={
            // ui-config: a URL scheme hint, not copy
            "https://"} style={input} />
      </Row>
    </div>
  );
}
